import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { PageHeader, Card, CardBody } from "../../components/ui";
import {
  FaRobot, FaArrowRight, FaArrowLeft, FaKey, FaDatabase, FaCommentDots,
  FaMicrophone, FaLightbulb, FaChevronRight, FaCheckCircle, FaExclamationTriangle,
  FaCog, FaBrain, FaVolumeUp
} from "react-icons/fa";

export default function AIFeaturesGuide() {
  useEffect(() => {
    document.title = "AI Chatbot & Voice Guide | Walytic - OpenAI, Knowledge Bases & Voice Notes";
  }, []);

  return (
    <div className="page-content">
      <PageHeader title="AI Chatbot & Voice Guide" subtitle="Connect OpenAI and ElevenLabs to build smart WhatsApp bots that answer questions and send voice notes." />

      <div className="max-w-4xl mx-auto">

        {/* Table of Contents */}
        <nav className="bg-surface-card rounded-xl border border-surface-border p-6 mb-12">
          <h2 className="font-semibold text-lg text-ink-primary mb-4">In this guide</h2>
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              { id: 'setting-up-openai', label: 'Setting up OpenAI' },
              { id: 'setting-up-elevenlabs', label: 'Setting up ElevenLabs' },
              { id: 'knowledge-bases', label: 'Knowledge bases (AI training)' },
              { id: 'ai-in-flow-builder', label: 'Using AI in Flow Builder' },
              { id: 'attachment-analysis', label: 'Reading documents, audio & images' },
              { id: 'ai-takeover', label: 'AI Takeover (autonomous routines)' },
              { id: 'group-follow-ups', label: 'Group follow-ups & AI replies' },
              { id: 'voice-notes', label: 'Voice notes' },
              { id: 'tips', label: 'Tips and best practices' },
            ].map((item) => (
              <a key={item.id} href={`#${item.id}`} className="flex items-center text-sm text-ink-secondary hover:text-brand py-1.5 transition-colors">
                <FaChevronRight className="h-4 w-4 mr-1 text-ink-muted" />
                {item.label}
              </a>
            ))}
          </div>
        </nav>

        {/* Section: Setting up OpenAI */}
        <section id="setting-up-openai" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Setting up OpenAI</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            All AI features in Walytic run through OpenAI. You need an API key before any AI node will work.
          </p>

          <div className="space-y-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Open AI settings</h3>
                <p className="text-ink-secondary">
                  Go to <strong>Settings</strong> in the left sidebar, then click the <strong>AI</strong> tab at the top of the page.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Enter your OpenAI API key</h3>
                <p className="text-ink-secondary mb-3">
                  Paste your key into the OpenAI API Key field. It starts with <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">sk-...</code>
                </p>
                <p className="text-ink-secondary">
                  You can get a key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">platform.openai.com/api-keys</a>. OpenAI charges per usage, not per month.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Save</h3>
                <p className="text-ink-secondary">
                  Click Save. Your key is stored encrypted on our servers. Nobody, including the Walytic team, can view the raw key after saving.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Setting up ElevenLabs */}
        <section id="setting-up-elevenlabs" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Setting up ElevenLabs</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            ElevenLabs is optional. You only need it if you want to send AI-generated voice notes via WhatsApp. OpenAI TTS is also available as an alternative.
          </p>

          <div className="space-y-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Go to the same AI settings tab</h3>
                <p className="text-ink-secondary">
                  <strong>Settings &gt; AI</strong> tab, scroll down to the ElevenLabs section.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Enter your ElevenLabs API key</h3>
                <p className="text-ink-secondary mb-3">
                  Paste the key. It starts with <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm">xi-...</code> and you can get one from your ElevenLabs dashboard.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Save and done</h3>
                <p className="text-ink-secondary">
                  Like OpenAI, this key is stored encrypted. ElevenLabs costs roughly $0.30 per 1,000 characters of generated speech.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Knowledge Bases */}
        <section id="knowledge-bases" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Knowledge bases (AI training)</h2>
          <p className="text-ink-secondary mb-4 leading-relaxed">
            Knowledge bases tell the AI what your business does, what products you sell, how to answer common questions, and anything else it needs to know. Without a knowledge base, the AI only has its general training data and your system prompt to work with.
          </p>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            You can create as many knowledge bases as you need. Each one covers a specific topic.
          </p>

          <h3 className="font-semibold text-xl text-ink-primary mb-4">Creating a knowledge base</h3>
          <div className="space-y-8 mb-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Go to Knowledge Base</h3>
                <p className="text-ink-secondary">
                  In the left sidebar, click <strong>Knowledge Base</strong>. Then click <strong>Create New</strong>.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Fill in the details</h3>
                <div className="bg-surface-card border border-surface-border rounded-xl overflow-hidden mt-3">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-surface-border">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold text-ink-primary">Field</th>
                        <th className="text-left py-3 px-4 font-semibold text-ink-primary">What to enter</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {[
                        ['Name', 'A short label like "Product FAQ" or "Pricing Info"'],
                        ['Type', 'Text (paste content directly) or CSV (upload a file)'],
                        ['Content', 'For Text type: free-form text describing your business, products, or policies. For CSV type: upload a .csv file with structured data.'],
                      ].map(([field, desc]) => (
                        <tr key={field}>
                          <td className="py-3 px-4 font-medium text-ink-primary">{field}</td>
                          <td className="py-3 px-4 text-ink-secondary">{desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Save the entry</h3>
                <p className="text-ink-secondary">
                  Once saved, the knowledge base becomes available inside Flow Builder&apos;s AI nodes.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>How selection works:</strong> In the &quot;Send to AI&quot; and &quot;AI Conversation&quot; nodes, you can pick specific knowledge bases from a dropdown. If you don&apos;t select any, the AI automatically uses all active knowledge bases.
            </p>
          </div>
        </section>

        {/* Section: AI in Flow Builder */}
        <section id="ai-in-flow-builder" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Using AI in Flow Builder</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            There are two AI nodes in Flow Builder. They do different things, so pick the right one for your use case.
          </p>

          {/* Send to AI */}
          <div className="bg-surface-card border border-surface-border rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <FaBrain className="h-5 w-5 text-purple-700" />
              </div>
              <h3 className="font-semibold text-xl text-ink-primary">Send to AI</h3>
            </div>
            <p className="text-ink-secondary mb-4">
              Sends a single message to OpenAI and gets one response back. Good for classification, one-off answers, or processing input before passing it to the next node.
            </p>
            <h4 className="font-semibold text-sm text-ink-primary mb-2">Configuration options:</h4>
            <ul className="space-y-2 text-sm text-ink-secondary">
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>System prompt</strong> -- tells the AI how to behave (e.g., &quot;You are a friendly customer support agent for a shoe store&quot;)</span>
              </li>
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>Knowledge base selection</strong> -- pick one or more KBs, or leave blank to use all</span>
              </li>
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>Silent mode</strong> -- when enabled, the AI response is not sent to the contact. Instead, it is stored as output so the next node can use it via <code className="bg-slate-100 px-1 rounded">{'{{prev.output}}'}</code>. Useful for classifying messages or extracting data before deciding what to do.</span>
              </li>
            </ul>
          </div>

          {/* AI Conversation */}
          <div className="bg-surface-card border border-surface-border rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <FaCommentDots className="h-5 w-5 text-violet-700" />
              </div>
              <h3 className="font-semibold text-xl text-ink-primary">AI Conversation</h3>
            </div>
            <p className="text-ink-secondary mb-4">
              Starts a multi-turn conversation between the AI and the contact. The AI keeps replying until the contact says an exit keyword or hits the turn limit.
            </p>
            <h4 className="font-semibold text-sm text-ink-primary mb-2">Configuration options:</h4>
            <ul className="space-y-2 text-sm text-ink-secondary">
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>System prompt</strong> -- same as Send to AI, but applied across the entire conversation</span>
              </li>
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>Knowledge base selection</strong> -- pick which KBs the AI can reference</span>
              </li>
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>Exit keyword</strong> -- a word or phrase (like &quot;stop&quot; or &quot;human&quot;) that ends the AI conversation and moves to the next node in the flow</span>
              </li>
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>Max turns</strong> -- the maximum number of back-and-forth exchanges before the node exits automatically</span>
              </li>
            </ul>
          </div>

          {/* AI Analyzer */}
          <div className="bg-surface-card border border-surface-border rounded-xl p-6 mt-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-lg bg-sky-100 flex items-center justify-center">
                <FaBrain className="h-5 w-5 text-sky-700" />
              </div>
              <h3 className="font-semibold text-xl text-ink-primary">AI Analyzer</h3>
            </div>
            <p className="text-ink-secondary mb-4">
              Reads an attachment the customer sends — a PDF, document, voice note, or image —
              turns it into text, and answers or extracts data from it. Use it on its own or
              alongside the other AI nodes.
            </p>
            <h4 className="font-semibold text-sm text-ink-primary mb-2">Configuration options:</h4>
            <ul className="space-y-2 text-sm text-ink-secondary">
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>Analysis instruction</strong> — what to look for, e.g. &quot;extract the invoice total and due date&quot;</span>
              </li>
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>Attachment types</strong> — restrict analysis to PDFs, audio, images, or any</span>
              </li>
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>Reply in chat</strong> — off keeps the analysis silent so the next node can use <code className="bg-slate-100 px-1 rounded">{'{{prev.output}}'}</code></span>
              </li>
            </ul>
            <p className="text-xs text-ink-muted mt-3">
              Tip: pair it with a trigger set to &quot;Any message&quot; so it still fires when a
              customer sends a file without any caption.
            </p>
          </div>

          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-6">
            <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
              <FaExclamationTriangle className="h-5 w-5" /> When to use which
            </h4>
            <ul className="space-y-2 text-sm text-amber-800">
              <li>- Use <strong>Send to AI</strong> when you need a single answer, a classification, or silent processing</li>
              <li>- Use <strong>AI Conversation</strong> when the contact should chat freely with the bot (support, FAQ, booking, etc.)</li>
              <li>- You can combine both: use Send to AI in silent mode to classify the message, then route to different AI Conversation nodes based on the topic</li>
            </ul>
          </div>
        </section>

        {/* Section: Attachment analysis */}
        <section id="attachment-analysis" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Reading documents, audio &amp; images</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            AI nodes can read what a customer sends. Turn this on per node with
            <strong> Read attachments</strong>, or use a dedicated <strong>AI Analyzer</strong> node
            when the whole job is understanding the file.
          </p>

          <div className="bg-surface-card border border-surface-border rounded-xl p-6 mb-6">
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'PDFs & documents', text: 'Text is extracted from the PDF and the AI answers from it — invoices, statements, brochures.' },
                { title: 'Audio & voice notes', text: 'Voice messages are transcribed, so the AI can answer a spoken question the same as a typed one.' },
                { title: 'Images', text: 'Photos and screenshots are read by the vision model — receipts, product shots, ID documents.' },
                { title: 'Silent output', text: 'Keep the analysis silent and feed it to the next node with a variable.' },
              ].map((item) => (
                <div key={item.title} className="bg-slate-50 rounded-lg p-4">
                  <h5 className="font-semibold text-sm text-ink-primary mb-1">{item.title}</h5>
                  <p className="text-sm text-ink-secondary">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Scanned PDFs with no text layer cannot be read, and video
              attachments are not analysed. The AI is told an attachment arrived so it can still
              respond gracefully.
            </p>
          </div>
        </section>

        {/* Section: AI Takeover */}
        <section id="ai-takeover" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">AI Takeover</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            Describe what you want in plain language and the AI builds the routines for you —
            then runs them on a schedule and answers messages on its own. Find it under
            <strong> AI Takeover</strong> on the Flow Builder page.
          </p>

          <div className="space-y-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">State your goal</h3>
                <p className="text-ink-secondary">
                  Pick the WhatsApp number, then write what you want in your own words — e.g.
                  &quot;Keep our VIP group warm with a weekly tip and answer pricing questions quickly.&quot;
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Answer a few questions</h3>
                <p className="text-ink-secondary">
                  If the goal is missing a key detail, the AI asks up to three short questions and
                  then proposes a complete plan.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <h3 className="font-semibold text-lg text-ink-primary mb-2">Review and activate</h3>
                <p className="text-ink-secondary">
                  Each routine has a title, an instruction, and a wall-clock schedule — daily,
                  weekly (pick the day and time), or an interval — in your timezone. Tweak any
                  of them, then <strong>Create &amp; activate</strong> or save as a draft.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-surface-card border border-surface-border rounded-xl p-6">
            <h4 className="font-semibold text-sm text-ink-primary mb-3">How it behaves once live</h4>
            <ul className="space-y-2 text-sm text-ink-secondary">
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>Routines post on schedule.</strong> If a routine has no fixed text, the AI writes a fresh post each run from the instruction, your voice, and your knowledge base.</span>
              </li>
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>It answers in between.</strong> Incoming messages in scope are answered automatically — every message, or only those matching keywords.</span>
              </li>
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>Humans always win.</strong> The moment a teammate posts in the chat, the AI goes quiet for the configured cooldown so it never talks over staff.</span>
              </li>
              <li className="flex items-start gap-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span><strong>You stay in control.</strong> Pause, re-activate, toggle individual routines, or send one immediately with the run-now button.</span>
              </li>
            </ul>
          </div>

          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Scheduled posts are cold outreach and count against your
              daily send cap; replies to incoming messages do not. Set a cooldown of 0 only if
              no one else handles the chat.
            </p>
          </div>
        </section>

        {/* Section: Group follow-ups */}
        <section id="group-follow-ups" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Group follow-ups &amp; AI replies</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            WhatsApp groups can be targeted directly — either with scheduled posts, or by letting
            the AI answer when a message matches your keywords. Find it under <strong>Group
            Follow-Ups</strong> on the Flow Builder page.
          </p>

          <div className="bg-surface-card border border-surface-border rounded-xl p-6 mb-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <h5 className="font-semibold text-sm text-ink-primary mb-2 flex items-center gap-2">
                  <FaCommentDots className="h-4 w-4 text-emerald-600" /> Scheduled posts
                </h5>
                <p className="text-sm text-ink-secondary">
                  Pick a group, write a message, and choose how often to post it — every few hours,
                  days, or on a schedule. Set a maximum number of sends or leave it running.
                </p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h5 className="font-semibold text-sm text-ink-primary mb-2 flex items-center gap-2">
                  <FaRobot className="h-4 w-4 text-fuchsia-600" /> AI replies
                </h5>
                <p className="text-sm text-ink-secondary">
                  Give the AI a set of trigger keywords and instructions. When a group message
                  matches, the AI replies in the group — in your brand voice and from your
                  knowledge base.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>In a flow:</strong> the <strong>Group Follow-Up</strong> node posts one message
              into selected groups the moment the flow reaches it — handy for order updates or
              announcements triggered by a customer action.
            </p>
          </div>
        </section>

        {/* Section: Voice Notes */}
        <section id="voice-notes" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-4">Voice notes</h2>
          <p className="text-ink-secondary mb-6 leading-relaxed">
            The Voice Note node in Flow Builder converts text into speech and sends it as a WhatsApp voice message. You can use either ElevenLabs or OpenAI TTS as the provider.
          </p>

          <div className="bg-surface-card border border-surface-border rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <FaVolumeUp className="h-5 w-5 text-orange-700" />
              </div>
              <h3 className="font-semibold text-xl text-ink-primary">Voice Note node</h3>
            </div>
            <p className="text-ink-secondary mb-4">
              Add a Voice Note node to your flow. Enter the text you want spoken (variables work here too), pick a provider, and choose a voice.
            </p>

            <h4 className="font-semibold text-sm text-ink-primary mb-3">Available voices:</h4>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <h5 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <FaMicrophone className="h-4 w-4 text-purple-600" /> ElevenLabs
                </h5>
                <p className="text-sm text-ink-secondary mb-2">
                  Uses a Voice ID. The default is &quot;Rachel&quot;. You can find other Voice IDs in your ElevenLabs dashboard under the Voice Library.
                </p>
                <p className="text-xs text-ink-muted">
                  Cost: ~$0.30 per 1,000 characters
                </p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <h5 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <FaMicrophone className="h-4 w-4 text-brand" /> OpenAI TTS
                </h5>
                <p className="text-sm text-ink-secondary mb-2">
                  Six built-in voices to choose from:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {['Alloy', 'Echo', 'Fable', 'Onyx', 'Nova', 'Shimmer'].map((voice) => (
                    <span key={voice} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{voice}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> You can chain an AI node before a Voice Note node. The AI generates a text reply, and the Voice Note node converts it to speech. Set the AI node to silent mode so only the voice message gets sent.
            </p>
          </div>
        </section>

        {/* Section: Tips */}
        <section id="tips" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold text-ink-primary mb-6">Tips and best practices</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Start with a simple system prompt', text: 'Write 2-3 sentences describing who the AI is and what it should do. You can refine later based on real conversations.' },
              { title: 'Test with yourself first', text: 'Send a message to your own number before activating the flow for real contacts. Check the AI output makes sense.' },
              { title: 'Keep knowledge bases focused', text: 'One KB per topic works better than one giant file. "Pricing" and "Return Policy" as separate entries let you assign them to different flows.' },
              { title: 'Use silent mode for routing', text: 'Send to AI in silent mode can classify a message (e.g., "support" vs "sales"), and a Condition node can route to different branches based on the output.' },
              { title: 'Set a reasonable max turns', text: 'For AI Conversation, 10-15 turns is usually enough. Without a limit, long conversations burn through your API credits quickly.' },
              { title: 'Include an exit path', text: 'Always set an exit keyword so contacts can reach a human agent. Something like "agent" or "human" works well.' },
            ].map((tip, i) => (
              <div key={i} className="bg-surface-card border border-surface-border rounded-lg p-4">
                <h4 className="font-semibold text-sm text-ink-primary mb-1">{tip.title}</h4>
                <p className="text-sm text-ink-secondary">{tip.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Prev/Next navigation */}
        <div className="flex items-center justify-between border-t border-surface-border pt-8">
          <Link to="/guide/flow-builder" className="text-sm font-medium text-brand hover:opacity-80 flex items-center gap-1">
            <FaArrowLeft className="h-4 w-4" /> Prev: Flow Builder
          </Link>
          <Link to="/guide/campaigns" className="text-sm font-medium text-brand hover:opacity-80 flex items-center gap-1">
            Next: Campaigns <FaArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
